import { html, View } from 'rune-ts';
import { getResizedUrl } from '@services/MarppleShop/shared/util/image.ts';
import klass from './CreatorShopMainBanner24MoView.module.scss';
import { LikeButtonView } from '@services/MarppleShop/shared/components/atoms/LikeButton/LikeButtonView.ts';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { goLogin } from '@services/MarppleShop/shared/util/goLogin.ts';
import {
  ThumbnailAdult18Icon,
  ThumbnailAdult19Icon,
  TrophySolidIcon,
} from '@services/MarppleShop/shared/components/atoms/Icon';
import axios from 'axios';
import { getSharedData } from '@services/MarppleShop/shared/util/rune.ts';
import { noop } from '@fxts/core';
import { classes } from '@services/MarppleShop/shared/util';
import { font } from '@services/MarppleShop/shared/typography';
import { ButtonView } from '@services/MarppleShop/shared/components/atoms/Button/ButtonView';
import { makePlusUrl } from '@services/MarppleShop/shared/util/url.ts';
import { CreatorShopHomeBottomSheetView } from '@services/MarppleShop/renderApp/components/cells/Header/CreatorShopHomeBottomSheetView.ts';
import { EBestCreatorRankingType } from '@services/MarppleShop/shared/enum/EBestCreatorRankingType.ts';
import { MuiF } from '@modules/Mui/F/Function/module/MuiF';
import { MShopAppStoreAboutEditModalMuiF } from '@modules/MShop/App/Store/About/EditModal/F/Mui/module/MShopAppStoreAboutEditModalMuiF';
import { CreatorConstantS, CreatorTypeS } from '@services/MarppleShop/features/Creator/outbound/share.ts';
import { MembershipUtilS } from '@services/MarppleShop/features/Membership/outbound/share.ts';
import { getMonth, parseISO } from 'date-fns';
import differenceInCalendarWeeks from 'date-fns/differenceInCalendarWeeks/index.js';
import startOfMonth from 'date-fns/startOfMonth/index.js';

const stripProtocolAndWww = (url: string) => url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '');

export type TCreatorShopMainBanner24MoViewProps = {
  cover: 'Default' | 'No Cover';
  show_adult_creator_thumbnail: boolean;
  creator: {
    id: number;
    thumbnail_img: string;
    domain_name: string;
    description: string;
    name: string;
    like_count: number;
    banner_img: string;
    sns_url: string;
    is_liked: boolean;
    user_id: number | null;
    banner_img_mo: string;
    is_adult: boolean;
    fan_letter_count: number;
  };
  is_owner: boolean | undefined;
  is_login: boolean;
  best_store_ranking:
    | {
        current_rank: number;
        previous_rank: number | null;
        date: string;
        type: EBestCreatorRankingType;
      }
    | undefined;
  membership:
    | {
        id: string;
        membership_existed: boolean;
        membership_plan_existed: boolean;
        membership_support_existed: boolean;
        ciety: { subdomain: string } | null;
        my: {
          memberships?: unknown[];
        };
      }
    | undefined;
};

/**
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=22309-16359&m=dev
 *
 * https://www.figma.com/design/Top00pi5q5ylSxEviOOciV/MarppleShop-Design-System?node-id=22309-16359&t=rXhWlepzxakOTEA8-0
 *
 * @see CreatorMainProfileView
 */
export class CreatorShopMainBanner24MoView extends View<TCreatorShopMainBanner24MoViewProps> {
  private like_button_view = new LikeButtonView({
    device: 'MO',
    state: this.data.creator.is_liked ? 'Click' : 'Unclick',
    theme: this.data.cover === 'Default' ? 'Dark' : 'Light',
    count: this.data.creator.like_count,
    onClick: () => this.handleLickButtonClick(),
  });

  private creator_shop_home_bottom_sheet_view = new CreatorShopHomeBottomSheetView(this.data);

  protected override onRender() {
    this.delegate('click', `.${klass.additional_links_count}`, () => {
      this.creator_shop_home_bottom_sheet_view.open();
    });

    this.delegate('click', `.${klass.ranking}`, () => {
      this.creator_shop_home_bottom_sheet_view.open();
    });

    this.delegate('click', `.${klass.creator_introduce}`, () => {
      this.creator_shop_home_bottom_sheet_view.open();
    });
  }

  private handleLickButtonClick = async () => {
    if (this.data.is_login) {
      const already_like = this.like_button_view.data.state === 'Click';
      this.like_button_view.element().setAttribute('disabled', '');

      try {
        await axios.post(UtilS.makeApiUrl(`/:lang/@api/stores_like`, { lang: T.lang }), {
          already_like,
          store_id: this.data.creator.id,
        });

        this.like_button_view.setState(already_like ? 'Unclick' : 'Click');
        this.like_button_view.setCount(
          already_like ? this.like_button_view.data.count - 1 : this.like_button_view.data.count + 1,
        );
      } catch {
        // https://www.figma.com/design/2M9d5FfwC5cighwGgLxqJN?node-id=4568-249548&m=dev#1347281378
        noop();
      } finally {
        this.like_button_view.element().removeAttribute('disabled');
      }
    } else {
      goLogin();
    }
  };

  private handleProfileEditButtonClick = () =>
    MuiF.openFrame(
      MShopAppStoreAboutEditModalMuiF.frame,
      (_f: unknown, _p: unknown, [t]: [{ makeData: () => { store: CreatorTypeS.CreatorMainProfile } }]) => {
        t.makeData = () => ({ store: this.data.creator });
      },
    );

  protected override template(props: TCreatorShopMainBanner24MoViewProps) {
    const { creator, show_adult_creator_thumbnail, membership, cover, best_store_ranking, is_owner } = props;

    const sns_href = membership?.ciety
      ? MembershipUtilS.getCietyUrl(`/@${membership.ciety.subdomain}`)
      : creator.sns_url;

    const sharedData = getSharedData(this);

    const ranking = best_store_ranking?.current_rank;

    const parsed_best_store_ranking_date = best_store_ranking ? parseISO(best_store_ranking.date) : null;

    const show_button_list =
      is_owner ||
      (membership?.membership_existed &&
        membership.membership_plan_existed &&
        !show_adult_creator_thumbnail) ||
      (membership?.membership_existed &&
        membership.membership_support_existed &&
        !show_adult_creator_thumbnail);

    return html`<div
      class="${classes(cover === 'Default' ? klass.default_banner : klass.no_cover_banner, {
        [klass.show_button_list]: show_button_list,
      })}"
    >
      ${cover === 'Default'
        ? html`<div class="${klass.gradient}">
            <img
              src="${getResizedUrl({
                url: creator.banner_img_mo,
                width: 1440 * 2,
                format: 'webp',
                quality: 80,
              })}"
              alt=""
              class="${klass.gradient_img}"
            />
          </div>`
        : null}

      <div
        class="${classes(
          cover === 'Default'
            ? klass.default_creator_shop_information_wrapper
            : klass.no_cover_creator_shop_information_wrapper,
          { [klass.has_description]: creator.description },
        )}"
      >
        <div class="${klass.creator_shop_information}">
          ${(() => {
            if (show_adult_creator_thumbnail) {
              return html`<div class="${klass.adult_creator_thumbnail}">
                ${ET.lang === 'kr' ? ThumbnailAdult19Icon : ThumbnailAdult18Icon}
              </div>`;
            } else if (creator.thumbnail_img && creator.thumbnail_img !== CreatorConstantS.thumbnail_img) {
              return html`<img
                alt=""
                src="${getResizedUrl({
                  url: creator.thumbnail_img,
                  width: 144 * 2,
                  format: 'webp',
                  quality: 80,
                })}"
                class="${klass.creator_profile_image}"
              />`;
            }

            return null;
          })()}

          <div
            class="${classes(klass.creator_profile_text, {
              [klass.has_creator_thumbnail]: show_adult_creator_thumbnail || creator.thumbnail_img,
            })}"
          >
            <div class="${klass.creator_name_text_wrapper}">
              <p
                class="${classes(font('18_bold'), klass.creator_name_text, {
                  [klass.default_cover]: cover === 'Default',
                })}"
              >
                ${html.preventEscape(creator.name)}
              </p>
            </div>

            ${best_store_ranking && parsed_best_store_ranking_date
              ? html`<button
                  class="${classes(klass.ranking, {
                    [klass.default_cover]: cover === 'Default',
                  })}"
                >
                  <div
                    class="${classes(klass.trophy_icon, {
                      [klass.default_cover]: cover === 'Default',
                    })}"
                  >
                    ${TrophySolidIcon}
                  </div>

                  <span
                    class="${classes(klass.rank_count, font('12_medium'), {
                      [klass.default_cover]: cover === 'Default',
                    })}"
                  >
                    ${{
                      [EBestCreatorRankingType.Monthly]: ET(
                        'creator_shop::creator_main_banner::monthly_ranking',
                        {
                          month: getMonth(parsed_best_store_ranking_date) + 1,
                          ranking,
                        },
                      ),
                      [EBestCreatorRankingType.Weekly]: ET(
                        'creator_shop::creator_main_banner::weekly_ranking',
                        {
                          month: getMonth(parsed_best_store_ranking_date) + 1,
                          week:
                            differenceInCalendarWeeks(
                              parsed_best_store_ranking_date,
                              startOfMonth(parsed_best_store_ranking_date),
                              { weekStartsOn: 0 },
                            ) + 1,
                          ranking,
                        },
                      ),
                      [EBestCreatorRankingType.Realtime]: ET(
                        'creator_shop::creator_main_banner::realtime_ranking',
                        {
                          ranking,
                        },
                      ),
                    }[best_store_ranking.type]}
                  </span>
                </button>`
              : null}
          </div>

          ${this.like_button_view}
        </div>

        ${creator.description || creator.sns_url || membership?.ciety
          ? html`
              <div class="${klass.sub}">
                ${creator.description
                  ? html`<button type="button" class="${klass.creator_introduce}">
                      <span
                        class="${classes(font('14_medium'), klass.creator_introduce_text, {
                          [klass.default_cover]: cover === 'Default',
                        })}"
                      >
                        ${html.preventEscape(creator.description)}
                      </span>

                      <span
                        class="${classes(
                          klass.see_more,
                          {
                            [klass.default_cover]: cover === 'Default',
                          },
                          font('14_medium'),
                        )}"
                      >
                        ${ET('creator_shop::creator_main_banner::see_more')}
                      </span>
                    </button>`
                  : null}
                ${creator.sns_url || membership?.ciety
                  ? html`<div class="${klass.link}">
                      <a
                        href="${sns_href}"
                        target="_blank"
                        class="${classes(font('12_medium'), klass.link_text, 'external-url', {
                          [klass.default_cover]: cover === 'Default',
                        })}"
                        >${html.preventEscape(stripProtocolAndWww(sns_href))}</a
                      >

                      <!-- https://www.figma.com/design/Top00pi5q5ylSxEviOOciV?node-id=23108-27970&m=dev#1347612635 -->
                      ${creator.sns_url && membership?.ciety
                        ? html`<button
                            type="button"
                            class="${classes(font('12_medium'), klass.additional_links_count, {
                              [klass.default_cover]: cover === 'Default',
                            })}"
                          >
                            ${ET('creator_shop::creator_main_banner::the_other_links', { count: 1 })}
                          </button>`
                        : null}
                    </div>`
                  : null}
              </div>
            `
          : null}
        ${show_button_list
          ? html`<div class="${klass.button_gap}">
              ${is_owner
                ? new ButtonView({
                    size: 40,
                    color: 'Secondary-A',
                    type: 'button',
                    label: ET('creator_shop::creator_main_banner::profile_edit'),
                    className: klass.button,
                    onClick: this.handleProfileEditButtonClick,
                  })
                : null}
              ${membership?.membership_existed &&
              membership.membership_plan_existed &&
              !show_adult_creator_thumbnail
                ? new ButtonView({
                    size: 40,
                    color: 'Secondary-A',
                    label: !membership.my.memberships?.at(0)
                      ? ET('mps2::membership::go_join_button_label')
                      : ET('mps2::membership::go_join_button_label_already'),
                    as: 'a',
                    href: makePlusUrl(
                      sharedData?.plus_crew?.domain_name,
                      `/${ET.lang}/${creator.domain_name}/membership/main`,
                    ),
                    className: klass.button,
                  })
                : null}
              ${membership?.membership_existed &&
              membership.membership_support_existed &&
              !show_adult_creator_thumbnail
                ? new ButtonView({
                    size: 40,
                    color: 'Secondary-A',
                    label: ET('mps2::membership::support::go_support_button_label'),
                    as: 'a',
                    href: makePlusUrl(
                      sharedData?.plus_crew?.domain_name,
                      `/${ET.lang}/${creator.domain_name}/membership/support`,
                    ),
                    className: klass.button,
                  })
                : null}
            </div>`
          : null}
      </div>

      ${this.creator_shop_home_bottom_sheet_view}
    </div>`;
  }
}
